import { useQueries } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { getAreaById } from "../../features/areas/api/areas";
import { getModelById } from "../../features/models/api/models-api";
import type { RecentlyViewedItem } from "./useRecentlyViewed";

interface ValidationResult {
  item: RecentlyViewedItem;
  exists: boolean;
  isLoading: boolean;
  serverData?: unknown;
}

/**
 * Hook to validate that recently viewed items still exist in the database.
 * Items that have been deleted will be automatically removed from local storage.
 *
 * @param items - Array of recently viewed items from local storage
 * @param removeItem - Function to remove an item from local storage
 * @returns Object containing validated items and loading state
 */
export const useValidateRecentlyViewed = (
  items: RecentlyViewedItem[],
  removeItem: (id: string, type: "area" | "model") => void,
  updateItem?: (
    id: string,
    type: "area" | "model",
    updates: Partial<Omit<RecentlyViewedItem, "id" | "type" | "viewedAt">>
  ) => void,
  updateModelAreaSlugs?: (areaId: string, newAreaSlug: string) => void
) => {
  // Track items that have been marked for removal to prevent duplicate removal calls
  const removedItemsRef = useRef<Set<string>>(new Set());

  // Create validation queries for each item
  const validationQueries = useQueries({
    queries: items.map((item) => ({
      queryKey: ["validate-recently-viewed", item.type, item.id],
      queryFn: async () => {
        try {
          if (item.type === "area") {
            const area = await getAreaById(item.id);
            return { exists: !!area, item, serverData: area };
          }
          const model = await getModelById(item.id);
          return { exists: !!model, item, serverData: model };
        } catch (error) {
          // If we get any error (including 404), treat as not existing
          // This is expected for deleted items - use debug level to reduce noise
          console.debug(
            `[useValidateRecentlyViewed] Item ${item.type} ${item.id} does not exist (likely deleted)`,
            error
          );
          return { exists: false, item };
        }
      },
      retry: false, // Don't retry failed requests
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    })),
  });

  // Create validation results
  const validationResults: ValidationResult[] = useMemo(() => {
    return items.map((item, index) => {
      const query = validationQueries[index];
      return {
        item,
        exists: query.data?.exists ?? true, // Default to true while loading
        isLoading: query.isLoading,
        serverData: query.data?.serverData,
      };
    });
  }, [items, validationQueries]);

  // Auto-cleanup deleted items from local storage
  const cleanupDeletedItems = useCallback(() => {
    for (const result of validationResults) {
      const itemKey = `${result.item.type}-${result.item.id}`;

      // Only remove if:
      // 1. We've finished loading
      // 2. The item doesn't exist
      // 3. We haven't already processed this item
      if (
        !result.isLoading &&
        !result.exists &&
        !removedItemsRef.current.has(itemKey)
      ) {
        console.info(
          `[useValidateRecentlyViewed] Removing deleted ${result.item.type}: ${result.item.name} (${result.item.id})`
        );
        removedItemsRef.current.add(itemKey);
        removeItem(result.item.id, result.item.type);
      }
    }
  }, [validationResults, removeItem]);

  // Sync updated names and slugs from server to local storage
  useEffect(() => {
    if (!updateItem) return;

    for (const result of validationResults) {
      if (result.isLoading || !result.exists || !result.serverData) continue;

      const { item, serverData } = result;
      const updates: Partial<
        Omit<RecentlyViewedItem, "id" | "type" | "viewedAt">
      > = {};
      let needsUpdate = false;

      if (item.name !== serverData.name) {
        updates.name = serverData.name;
        needsUpdate = true;
      }

      if (item.slug !== serverData.slug) {
        updates.slug = serverData.slug;
        needsUpdate = true;
      }

      if (item.type === "model" && item.area_id !== serverData.area_id) {
        updates.area_id = serverData.area_id;
        needsUpdate = true;
      }

      // If this is an area, and its slug changed, update all recently viewed models in this area
      if (
        item.type === "area" &&
        needsUpdate &&
        updates.slug &&
        updateModelAreaSlugs
      ) {
        updateModelAreaSlugs(item.id, updates.slug);
      }

      if (needsUpdate) {
        updateItem(item.id, item.type, updates);
      }
    }
  }, [validationResults, updateItem, updateModelAreaSlugs]);

  // Run cleanup when validation results change
  useEffect(() => {
    cleanupDeletedItems();
  }, [cleanupDeletedItems]);

  // Filter to only valid (existing) items, applying server updates if available
  const validItems = useMemo(() => {
    return validationResults
      .filter((result) => result.exists || result.isLoading)
      .map((result) => {
        if (result.serverData) {
          return {
            ...result.item,
            name: result.serverData.name,
            slug: result.serverData.slug,
            area_id:
              result.item.type === "model"
                ? result.serverData.area_id
                : result.item.area_id,
          };
        }
        return result.item;
      });
  }, [validationResults]);

  // Overall loading state
  const isLoading = validationQueries.some((q) => q.isLoading);

  // Check if any items were removed
  const removedCount = useMemo(() => {
    return validationResults.filter(
      (result) => !result.isLoading && !result.exists
    ).length;
  }, [validationResults]);

  return {
    validItems,
    isLoading,
    removedCount,
    validationResults,
  };
};
